/**
 * Addons inside a party.
 *
 * The owner (or an officer) switches library addons on for the party; every
 * member then runs the same addon, reading and writing the party's copy of
 * its state, so a bracket, a veto or a rank board is live for the whole room.
 * Only Pugo's own library addons can run here - a party never carries code.
 *
 * On a desktop the addons get the big area where the chat is - an Addons tab
 * beside Party chat - because a bracket or a strat board needs room. The rail
 * keeps a small launcher: what is running, and the switches to add more.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Party, PartyAddonState } from '@/lib/types';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { Addon } from '@/lib/addons';
import { PARTY_ADDONS } from '@/lib/addonLibrary';
import { useSession } from '@/stores/session';
import { Icon } from '@/icons/Icon';
import { AddonFrame, type PartyAddonContext } from './Addons';
import { Switch } from './Switch';
import { useToast } from './ui';

/** Puts a changed addon into the cached party, so the switch moves at once. */
export function mergePartyAddon(
  queryClient: ReturnType<typeof useQueryClient>,
  partyId: string,
  key: string,
  addon: PartyAddonState | null
) {
  queryClient.setQueryData(['party', partyId], (old: { party: Party } | undefined) => {
    if (!old?.party) return old;
    const addons = { ...(old.party.addons ?? {}) };
    if (addon) addons[key] = addon;
    else delete addons[key];
    return { ...old, party: { ...old.party, addons } };
  });
}

/** The addons switched on in a party, in library order. */
export function runningAddons(party: Party) {
  const addons = party.addons ?? {};
  return PARTY_ADDONS.filter((a) => addons[a.id]?.enabled);
}

/* One stable addon object per key: a new object would reload the frame. The
   storage id is per library addon, not per party, so personal data - your
   rank history - follows you from party to party. */
const FRAMES: Record<string, Addon> = Object.fromEntries(PARTY_ADDONS.map((a) => [
  a.id,
  { id: 'party-lib-' + a.id, name: a.name, author: 'Pugo', description: a.description, code: a.code, enabled: true, addedAt: 0 }
]));

function useToggle(party: Party) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  async function toggle(key: string, enabled: boolean) {
    setBusy(key);
    try {
      const res = await api.post<{ addon: PartyAddonState }>('/api/parties/' + party.id + '/addons', { key, enabled });
      mergePartyAddon(queryClient, party.id, key, res.addon);
      if (enabled) toast(PARTY_ADDONS.find((a) => a.id === key)?.name + ' is live for the party', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }
  return { busy, toggle };
}

function useContexts(party: Party, canManage: boolean) {
  const me = useSession((s) => s.me);
  const members = useMemo(() => party.members
    .filter((m) => m.user)
    .map((m) => ({ id: m.userId, name: m.user!.displayName || m.user!.username, username: m.user!.username })), [party.members]);
  return (key: string): PartyAddonContext | null => {
    const state = party.addons?.[key];
    if (!me || !state) return null;
    return {
      partyId: party.id,
      key,
      gameId: party.gameId,
      hostId: party.hostId,
      meId: me.id,
      /*
       * Everybody in the party, unless the host has locked them.
       *
       * Addons used to be the host's toy: a member got the same panel with
       * every control stripped out of it, which for most of them is an empty
       * box with a LIVE badge on top. The server enforces the same rule.
       */
      canEdit: canManage || party.addonsOpen !== false,
      members,
      shared: state.shared ?? {},
      players: state.players ?? {}
    };
  };
}

/** The switches for every party addon. */
function Picker({ party, compact }: { party: Party; compact?: boolean }) {
  const { busy, toggle } = useToggle(party);
  const addons = party.addons ?? {};
  /* Empty while the library is being rebuilt - see addonLibrary.ts. An
     empty <ul> looks like a list that failed to load. */
  if (!PARTY_ADDONS.length) {
    return (
      <p className="m-0 rounded-[var(--radius-s)] bg-surface-2 px-3 py-2.5 text-[12.5px] text-mute">
        No party addons at the moment.
      </p>
    );
  }
  return (
    <ul className={cn('m-0 grid list-none p-0', compact ? 'gap-1.5' : 'gap-2 [grid-template-columns:repeat(auto-fill,minmax(260px,1fr))]')}>
      {PARTY_ADDONS.map((a) => {
        const on = !!addons[a.id]?.enabled;
        return (
          <li key={a.id} className={cn('flex items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2', compact ? 'px-2.5 py-2' : 'p-3')}>
            <span className={cn('grid flex-none place-items-center rounded-[9px]', compact ? 'h-8 w-8' : 'h-10 w-10', on ? 'bg-accent text-accent-fg' : 'bg-surface-3 text-mute')}>
              <Icon name={a.icon} size={compact ? 16 : 19} />
            </span>
            <span className="min-w-0 flex-1">
              <span className={cn('block truncate font-bold text-ink', compact ? 'text-[13px]' : 'text-[14px]')}>{a.name}</span>
              <span className="block text-[11.5px] leading-snug text-mute">{a.partyNote}</span>
            </span>
            <Switch size="sm" checked={on} disabled={busy === a.id} onChange={(v) => void toggle(a.id, v)} label={(on ? 'Turn off ' : 'Turn on ') + a.name} />
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Whether everybody may work the addons, or only the host.
 *
 * Open is the default and the copy says what changes, because the difference
 * is invisible from the host's own screen - they can always edit, so nothing
 * they see tells them what the other four are looking at.
 */
function OpenLock({ party }: { party: Party }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const open = party.addonsOpen !== false;

  async function set(next: boolean) {
    setBusy(true);
    try {
      await api.post('/api/parties/' + party.id + '/addons/open', { open: next });
      void queryClient.invalidateQueries({ queryKey: ['party', party.id] });
      toast(next ? 'Everyone in the party can use the addons' : 'Only you and your officers can change them', 'good');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-2.5 mb-2 flex items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] bg-surface-2 px-2.5 py-2">
      <Icon name={open ? 'users' : 'lock'} size={14} className="flex-none text-mute" />
      <span className="min-w-0 flex-1 text-[12px] leading-snug text-dim">
        {open ? 'Everyone in the party can use them' : 'Locked - only you and your officers'}
      </span>
      <Switch
        size="sm"
        checked={open}
        disabled={busy}
        onChange={(v) => void set(v)}
        label={open ? 'Lock the addons to the host' : 'Let everyone use the addons'}
      />
    </div>
  );
}

/**
 * The rail's part: what is running, one tap to open it in the big area, and
 * - for the owner - the switches.
 */
export function PartyAddonLauncher({ party, canManage, activeKey, onOpen }: {
  party: Party;
  canManage: boolean;
  /** The addon open in the big area, if the Addons tab is showing. */
  activeKey: string | null;
  onOpen: (key: string) => void;
}) {
  const [picking, setPicking] = useState(false);
  const running = runningAddons(party);

  return (
    <section aria-labelledby="party-addons-title" className="pugo-panel flex flex-none flex-col rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <header className="flex items-center gap-2 px-3.5 pt-3 pb-2">
        <Icon name="plug" size={15} className="flex-none text-accent" />
        <h2 id="party-addons-title" className="m-0 flex-1 text-[14.5px] font-bold text-ink">Party addons</h2>
        {running.length ? (
          <span className="flex items-center gap-1 text-[11.5px] font-semibold text-good">
            <i aria-hidden className="h-1.5 w-1.5 rounded-full bg-good" />
            {running.length} live
          </span>
        ) : null}
        {canManage ? (
          <button
            type="button"
            aria-expanded={picking}
            onClick={() => setPicking((v) => !v)}
            className={cn(
              'flex h-8 flex-none items-center gap-1.5 rounded-[calc(10px*var(--radius-mul,1))] px-2.5 text-[12.5px] font-bold outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--accent)]',
              picking ? 'bg-accent text-accent-fg' : 'bg-accent-soft text-accent hover:bg-[color-mix(in_srgb,var(--accent)_22%,transparent)]'
            )}
          >
            <Icon name={picking ? 'check' : 'plus'} size={14} />
            {picking ? 'Done' : 'Add'}
          </button>
        ) : null}
      </header>

      {/* Who may work them. A party is people playing together, so this is
          open unless somebody has a reason to close it - a bracket with one
          referee, usually. */}
      {canManage && (running.length || picking) ? <OpenLock party={party} /> : null}

      <div className="flex flex-col gap-1.5 px-2.5 pb-2.5">
        {picking ? <Picker party={party} compact /> : running.length ? (
          running.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => onOpen(a.id)}
              className={cn(
                'flex items-center gap-2.5 rounded-[calc(12px*var(--radius-mul,1))] px-2.5 py-2 text-left outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                activeKey === a.id ? 'bg-accent-soft' : 'bg-surface-2 hover:bg-surface-3'
              )}
            >
              <span className={cn('grid h-8 w-8 flex-none place-items-center rounded-[9px]', activeKey === a.id ? 'bg-accent text-accent-fg' : 'bg-surface-3 text-accent')}>
                <Icon name={a.icon} size={16} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-bold text-ink">{a.name}</span>
                <span className="block truncate text-[11.5px] text-mute">{a.tagline}</span>
              </span>
              <span className={cn('flex-none text-[12px] font-bold', activeKey === a.id ? 'text-accent' : 'text-mute')}>
                {activeKey === a.id ? 'Open' : 'Show'}
              </span>
            </button>
          ))
        ) : (
          <p className="m-0 px-1.5 py-1.5 text-[12.5px] leading-snug text-mute">
            {canManage
              ? 'Add a bracket, map veto, randomizer, strats or a rank board - everyone in the party gets it live.'
              : 'Nothing running yet. When the host switches one on it appears here for everybody.'}
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * The big area: the running addons, each with the whole width and height,
 * one at a time behind tabs. Every one stays running while hidden, so
 * switching never restarts a veto or a timer.
 */
export function PartyAddonStage({ party, canManage, selected, onSelect, hidden }: {
  party: Party;
  canManage: boolean;
  selected: string | null;
  onSelect: (key: string) => void;
  hidden?: boolean;
}) {
  const running = runningAddons(party);
  const contextFor = useContexts(party, canManage);
  const shown = running.some((a) => a.id === selected) ? selected : running[0]?.id ?? null;

  /*
   * Which addons are actually running as documents.
   *
   * Every enabled addon used to be mounted and the hidden ones merely styled
   * away, so six addons meant six sandboxed documents in memory while you
   * looked at one. The one on screen is kept, and the one before it, so
   * flicking between two tabs is instant; the rest are built again when you
   * come back to them, which is quick - the party's copy of their state comes
   * from the server, not from the frame.
   */
  const [recent, setRecent] = useState<string[]>(() => (shown ? [shown] : []));
  useEffect(() => {
    if (!shown) return;
    setRecent((list) => (list[0] === shown ? list : [shown, ...list.filter((x) => x !== shown)].slice(0, 2)));
  }, [shown]);
  /* Chat-only? Then not even the one on screen needs to exist. */
  const live = new Set(hidden ? [] : recent.length ? recent : shown ? [shown] : []);

  return (
    <div
      className={cn(
        'smooth-clip min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-[calc(20px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]',
        hidden ? 'hidden' : 'flex'
      )}
    >
      {running.length ? (
        <>
          {running.length > 1 ? (
            <div role="tablist" aria-label="Running addons" className="no-scrollbar flex flex-none gap-1 overflow-x-auto border-b border-line-soft px-2.5 py-2">
              {running.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  role="tab"
                  aria-selected={shown === a.id}
                  onClick={() => onSelect(a.id)}
                  className={cn(
                    'flex h-8 flex-none items-center gap-1.5 rounded-[calc(10px*var(--radius-mul,1))] px-3 text-[13px] font-semibold outline-none transition-colors focus-visible:shadow-[0_0_0_2px_var(--accent)]',
                    shown === a.id ? 'bg-accent-soft text-accent' : 'text-mute hover:bg-surface-2 hover:text-ink'
                  )}
                >
                  <Icon name={a.icon} size={14} />
                  {a.name}
                </button>
              ))}
            </div>
          ) : null}
          <div className="flex min-h-0 flex-1 flex-col p-2.5">
            {running.map((a) => {
              const ctx = contextFor(a.id);
              if (!ctx || !live.has(a.id)) return null;
              return (
                <div key={a.id} className={cn('min-h-0 flex-1 flex-col', shown === a.id ? 'flex' : 'hidden')}>
                  <AddonFrame addon={FRAMES[a.id]!} party={ctx} badge="Live" stretch />
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <h3 className="m-0 text-[16px] font-bold text-ink">{canManage ? 'Add an addon to the party' : 'No addons running'}</h3>
          <p className="mt-1 mb-3 text-[13.5px] text-mute">
            {canManage ? 'Switch one on and everyone in the party gets it, live - and everyone can use it.' : 'When the host switches one on it appears here, live, for everybody in the party.'}
          </p>
          {canManage ? <Picker party={party} /> : null}
        </div>
      )}
    </div>
  );
}

/** For the phone layout: the switches and the addons stacked in the details. */
export function PartyAddons({ party, canManage }: { party: Party; canManage: boolean }) {
  const [picking, setPicking] = useState(false);
  const running = runningAddons(party);
  const contextFor = useContexts(party, canManage);

  return (
    <section aria-labelledby="party-addons-title-inline" className="pugo-panel flex flex-col gap-2 rounded-[calc(18px*var(--radius-mul,1))] bg-surface-1 p-3 shadow-[inset_0_0_0_1px_var(--line-soft)]">
      <header className="flex items-center gap-2">
        <Icon name="plug" size={15} className="text-accent" />
        <h2 id="party-addons-title-inline" className="m-0 flex-1 text-[14.5px] font-bold text-ink">Party addons</h2>
        {canManage ? (
          <button type="button" onClick={() => setPicking((v) => !v)} className="h-8 rounded-[9px] bg-accent-soft px-2.5 text-[12.5px] font-bold text-accent">
            {picking ? 'Done' : 'Add'}
          </button>
        ) : null}
      </header>
      {picking ? <Picker party={party} compact /> : null}
      {running.map((a) => {
        const ctx = contextFor(a.id);
        return ctx ? <AddonFrame key={a.id} addon={FRAMES[a.id]!} maxHeight={440} party={ctx} badge="Live" /> : null;
      })}
      {!running.length && !picking ? (
        <p className="m-0 text-[12.5px] text-mute">{canManage ? 'Add a bracket, map veto, randomizer, strats or a rank board.' : 'The host has not added any addons yet.'}</p>
      ) : null}
    </section>
  );
}
